import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaSignOutAlt } from "react-icons/fa";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

const DERIV_WS_URL = "wss://api.derivws.com/trading/v1/options/ws/public";
const VOLATILITY_SYMBOL_PATTERN = /^(R_\d+|1HZ\d+V)$/;

const scaleConfidence = (raw) => Math.round(85 + (raw / 100) * 14);

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [ticks, setTicks] = useState({});
  const [signals, setSignals] = useState({});
  const [phase, setPhase] = useState("countdown");
  const [timer, setTimer] = useState(15);
  const ticksRef = useRef({});
  const marketsRef = useRef([]);

  useEffect(() => {
    ticksRef.current = ticks;
  }, [ticks]);

  useEffect(() => {
    marketsRef.current = markets;
  }, [markets]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlUid = params.get("uid");
    if (urlUid) {
      localStorage.setItem("userUid", urlUid);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const loginid = localStorage.getItem("currentDerivLogin");
      if (!loginid) {
        navigate("/login");
        return;
      }

      try {
        const usersCol = collection(db, "deriv_users");
        const q = query(usersCol, where("loginid", "==", loginid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          localStorage.removeItem("currentDerivLogin");
          navigate("/login");
          return;
        }

        const data = querySnapshot.docs[0].data();
        if (data.approved || data.approvals?.matches) {
          setUser(data);
        } else {
          navigate("/pending");
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        localStorage.removeItem("currentDerivLogin");
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const ws = new WebSocket(DERIV_WS_URL);

    ws.onopen = () => {
      ws.send(JSON.stringify({ active_symbols: "brief", req_id: 1 }));
    };

    ws.onmessage = (e) => {
      const res = JSON.parse(e.data);

      if (res.msg_type === "active_symbols" && Array.isArray(res.active_symbols)) {
        const volatilities = res.active_symbols
          .filter(
            (s) =>
              VOLATILITY_SYMBOL_PATTERN.test(s.underlying_symbol) &&
              s.exchange_is_open &&
              !s.is_trading_suspended
          )
          .map((s) => ({ symbol: s.underlying_symbol, name: s.underlying_symbol_name }))
          .sort((a, b) => a.symbol.localeCompare(b.symbol));

        setMarkets(volatilities);
        volatilities.forEach(({ symbol }) =>
          ws.send(JSON.stringify({ ticks: symbol, subscribe: 1 }))
        );
        return;
      }

      if (res.tick?.quote !== undefined) {
        const symbol = res.tick.symbol;
        const digit = Math.floor(res.tick.quote * 10) % 10;
        setTicks((prev) => ({
          ...prev,
          [symbol]: [...(prev[symbol] || []), digit].slice(-50),
        }));
      }
    };

    ws.onerror = (err) => console.error("Deriv WS error:", err);

    return () => ws.close();
  }, []);

  useEffect(() => {
    const generateAllSignals = () => {
      marketsRef.current.forEach(({ symbol }) => {
        const history = ticksRef.current[symbol];
        const recent = history && history.length > 0 ? history.slice(-50) : [Math.floor(Math.random() * 10)];
        const freq = Array(10).fill(0);
        recent.forEach((d) => freq[d]++);

        let maxCount = 0;
        let bestDigit = 0;
        freq.forEach((count, digit) => {
          if (count > maxCount) {
            maxCount = count;
            bestDigit = digit;
          }
        });

        const dominance = maxCount / recent.length;
        const confidence = scaleConfidence(dominance * 100);
        setSignals((prev) => ({
          ...prev,
          [symbol]: { digit: bestDigit, confidence },
        }));
      });
    };

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (phase === "countdown") {
            setPhase("entry");
            generateAllSignals();
            return 5;
          }
          if (phase === "entry") {
            setPhase("trade");
            return 5;
          }
          if (phase === "trade") {
            setPhase("countdown");
            return 15;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  const handleLogout = () => {
    localStorage.removeItem("currentDerivLogin");
    navigate("/login");
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f1514] text-slate-300">
        Loading Matches Pro...
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#0f1514] text-white">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-[#131b1a]/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="Matches Pro" className="h-10 w-10 rounded-full border border-white/10 object-cover" />
            <div>
              <h1 className="text-lg font-black tracking-[0.14em] text-[#A87F23]">MATCHES PRO</h1>
              <p className="text-xs uppercase tracking-[0.25em] text-[#819C97]">Live dashboard</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {user.approvals?.overUnder && (
              <Link
                to="/over-under"
                className="hidden rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:border-[#A87F23]/40 hover:text-[#A87F23] sm:inline-flex"
              >
                Over/Under
              </Link>
            )}
            {user.approvals?.evenOdd && (
              <Link
                to="/even-odd"
                className="hidden rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:border-[#A87F23]/40 hover:text-[#A87F23] sm:inline-flex"
              >
                Even/Odd
              </Link>
            )}
            <span className="hidden text-sm text-slate-400 sm:block">
              {user.name || user.email?.split("@")[0] || "User"}
            </span>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm transition hover:border-[#A87F23]/50 hover:text-[#A87F23]"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      {(user.approvals?.overUnder || user.approvals?.evenOdd) && (
        <div className="flex gap-2 overflow-x-auto border-b border-white/10 bg-[#131b1a] px-4 py-2.5 sm:hidden">
          <span className="shrink-0 self-center text-xs text-slate-500">Switch tool:</span>
          {user.approvals?.overUnder && (
            <Link to="/over-under" className="shrink-0 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-slate-300 transition hover:border-[#A87F23]/40 hover:text-[#A87F23]">
              Over/Under
            </Link>
          )}
          {user.approvals?.evenOdd && (
            <Link to="/even-odd" className="shrink-0 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-slate-300 transition hover:border-[#A87F23]/40 hover:text-[#A87F23]">
              Even/Odd
            </Link>
          )}
        </div>
      )}

      <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-8 rounded-[2rem] border border-white/10 bg-[#17201f] p-6">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">Matches Pro rhythm</p>
          <h2 className="mt-3 text-3xl font-black text-white">Focused Matches monitoring with confidence and timing.</h2>
          <p className="mt-3 max-w-3xl text-slate-300">
            The dashboard now carries the Matches Pro brand throughout the experience.
          </p>
        </div>

        {markets.length === 0 && (
          <p className="mb-6 text-center text-sm text-slate-400">Connecting to Deriv and loading live markets…</p>
        )}

        <main className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {markets.map(({ name, symbol }) => {
            const signal = signals[symbol] || {};
            const showEntry = phase === "entry" && signal.digit !== undefined;
            const showTrade = phase === "trade" && signal.digit !== undefined;

            return (
              <div
                key={symbol}
                className="rounded-[1.75rem] border border-white/10 bg-[#17201f]/90 p-4 text-center shadow-lg shadow-black/20"
              >
                <h3 className="mb-4 text-sm font-semibold text-[#cfd7d5]">{name}</h3>

                {showEntry || showTrade ? (
                  <>
                    <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-[1.5rem] bg-[#A87F23] text-4xl font-black text-[#0f1514]">
                      {signal.digit}
                    </div>

                    <p className="text-3xl font-black text-white">{signal.confidence}%</p>
                    {showEntry ? (
                      <p className="mt-3 text-base font-semibold text-[#A87F23]">Entry in {timer}s</p>
                    ) : (
                      <p className="mt-3 text-xl font-black text-[#819C97]">TRADE</p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-[1.5rem] border border-white/10 bg-[#0f1514] text-4xl font-black text-slate-400">
                      {timer}s
                    </div>
                    <p className="text-sm text-slate-400">Next Matches Pro cycle</p>
                  </>
                )}
              </div>
            );
          })}
        </main>
      </div>

      <footer className="mt-auto border-t border-white/10 py-6 text-center text-xs text-slate-500">
        © 2026 Matches Pro. Trading involves risk and requires personal judgment.
      </footer>
    </div>
  );
}
